import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Receipt } from 'lucide-react';

const TAX_FAQS = [
  { q: 'Is there zero tax up to ₹7 lakh in India?', a: 'Yes — under the new regime for FY 2025-26, Section 87A rebate gives zero tax for income up to ₹7 lakh. Above ₹7L, tax applies on the full income per slabs.' },
  { q: 'Which regime is better — new or old?', a: 'New regime is better if total deductions (80C + HRA + home loan) are under ₹3.5–4L. Old regime wins with a home loan + max 80C + high HRA city.' },
  { q: 'What is the ITR filing deadline?', a: 'July 31 of the assessment year (e.g., July 31, 2026 for FY 2025-26). Belated return until December 31 with a late fee of ₹5,000.' },
  { q: 'What is TDS?', a: 'Tax Deducted at Source — your employer deducts tax before paying your salary. It is advance payment of your annual tax. Excess TDS is refunded when you file ITR.' },
  { q: 'How do I save income tax under new regime?', a: 'The new regime has fewer deductions. You can still save via NPS (80CCD(2) — employer contribution, no limit), and claim standard deduction of ₹50,000.' },
  { q: 'Do I need to file ITR below ₹5 lakh?', a: 'Filing is mandatory above ₹2.5L (old) or ₹3L (new). Filing even with zero tax is recommended — it builds financial history for loans and visas.' },
];

export default function CalcTax() {
  document.title = 'Income Tax Calculator India FY 2025-26 — Budget Mantra';
  const [income, setIncome] = useState('');
  const [regime, setRegime] = useState('new');
  const [result, setResult] = useState(null);

  const calcNew = (i) => {
    if (i <= 300000)  return 0;
    if (i <= 700000)  return (i - 300000) * 0.05;
    if (i <= 1000000) return 20000 + (i - 700000) * 0.10;
    if (i <= 1200000) return 50000 + (i - 1000000) * 0.15;
    if (i <= 1500000) return 80000 + (i - 1200000) * 0.20;
    return 140000 + (i - 1500000) * 0.30;
  };

  const calcOld = (i) => {
    const ti = Math.max(0, i - 50000);
    if (ti <= 250000)  return 0;
    if (ti <= 500000)  return (ti - 250000) * 0.05;
    if (ti <= 1000000) return 12500 + (ti - 500000) * 0.20;
    return 112500 + (ti - 1000000) * 0.30;
  };

  const calculate = () => {
    const i = parseFloat(income);
    if (!i) return;
    const tax  = regime === 'new' ? calcNew(i) : calcOld(i);
    const cess = tax * 0.04;
    setResult({ tax, cess, total: tax + cess, monthly: (tax + cess) / 12 });
  };

  const fmt = (n) => Math.round(Math.max(0, n)).toLocaleString('en-IN');

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
          <Receipt size={20} className="text-orange-500" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold text-stone-900">Income Tax Calculator</h1>
          <p className="text-sm text-stone-500">FY 2025-26 · New vs Old regime</p>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4 max-w-md">
        <div>
          <label className="block text-xs font-bold text-stone-500 mb-1.5">Annual Income (₹)</label>
          <input type="number" value={income} onChange={e => setIncome(e.target.value)} placeholder="1000000"
            className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-orange-400" />
        </div>
        <div>
          <label className="block text-xs font-bold text-stone-500 mb-2">Tax Regime</label>
          <div className="flex gap-3">
            {['new', 'old'].map(r => (
              <button key={r} onClick={() => setRegime(r)}
                className={`flex-1 py-2 rounded-xl text-sm font-semibold border transition-colors
                  ${regime === r ? 'bg-orange-500 border-orange-500 text-white' : 'border-stone-200 text-stone-600 hover:border-orange-300'}`}>
                {r === 'new' ? 'New Regime' : 'Old Regime'}
              </button>
            ))}
          </div>
        </div>
        <button onClick={calculate}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-2.5 rounded-xl transition-colors">
          Calculate Tax
        </button>
        {result && (
          <div className="bg-orange-50 rounded-xl p-4 space-y-2">
            <div className="text-center mb-3">
              <p className="text-xs text-stone-500 mb-1">Total Tax + Cess</p>
              <p className="text-3xl font-extrabold text-orange-600">₹{fmt(result.total)}</p>
              <p className="text-xs text-stone-500 mt-1">≈ ₹{fmt(result.monthly)} / month</p>
            </div>
            <div className="flex justify-between text-xs text-stone-500">
              <span>Income tax</span><span className="font-semibold text-stone-700">₹{fmt(result.tax)}</span>
            </div>
            <div className="flex justify-between text-xs text-stone-500">
              <span>Health & education cess (4%)</span><span className="font-semibold text-stone-700">₹{fmt(result.cess)}</span>
            </div>
          </div>
        )}
      </div>
      <p className="mt-6 text-sm text-stone-500">
        Plan your taxes better →{' '}
        <Link to="/login" className="text-orange-500 font-semibold hover:underline">Sign up free</Link>
      </p>
      <div className="mt-10 max-w-2xl space-y-8 text-stone-700 dark:text-stone-300">
        <section>
          <h2 className="text-lg font-extrabold text-stone-900 dark:text-stone-100 mb-2">Income Tax in India — FY 2025-26</h2>
          <p className="text-sm leading-7">Income tax is levied on your annual income by the Central Government. For FY 2025-26, you can choose between the New Regime (lower rates, fewer deductions) and the Old Regime (higher rates, allows 80C/HRA/home loan deductions). Most salaried employees benefit from the new regime unless they have large deductions.</p>
        </section>
        <section>
          <h2 className="text-lg font-extrabold text-stone-900 dark:text-stone-100 mb-2">New Regime Slabs (FY 2025-26)</h2>
          <p className="text-sm leading-7">₹0–3L: Nil · ₹3–7L: 5% · ₹7–10L: 10% · ₹10–12L: 15% · ₹12–15L: 20% · Above ₹15L: 30%. Plus 4% Health & Education Cess. Income up to ₹7L = zero tax due to Section 87A rebate.</p>
        </section>
        <section>
          <h2 className="text-lg font-extrabold text-stone-900 dark:text-stone-100 mb-3">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {TAX_FAQS.map(faq => (
              <div key={faq.q} className="border-b border-stone-100 dark:border-stone-800 pb-4">
                <p className="text-sm font-bold text-stone-900 dark:text-stone-100 mb-1">{faq.q}</p>
                <p className="text-sm leading-6 text-stone-600 dark:text-stone-400">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2 className="text-lg font-extrabold text-stone-900 dark:text-stone-100 mb-2">Related Calculators</h2>
          <div className="flex flex-wrap gap-3">
            <Link to="/calc/emi" className="text-sm font-semibold text-orange-500 hover:underline">EMI Calculator →</Link>
            <Link to="/calc/sip" className="text-sm font-semibold text-orange-500 hover:underline">SIP Calculator →</Link>
            <Link to="/calc/salary" className="text-sm font-semibold text-orange-500 hover:underline">In-Hand Salary →</Link>
          </div>
        </section>
        <section className="bg-orange-50 dark:bg-orange-950/30 rounded-2xl p-5">
          <p className="text-xs font-bold text-orange-500 uppercase tracking-wide mb-1">Chanakya says</p>
          <p className="text-sm leading-6 text-stone-700 dark:text-stone-300 mb-4">
            {result
              ? `₹${fmt(result.monthly)}/month in taxes. Want me to check which regime saves you more based on your actual salary and investments?`
              : `Enter your income above and I'll tell you which tax regime saves you more money this year.`}
          </p>
          <Link to="/signup" className="inline-block bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold px-5 py-2 rounded-xl transition-colors">
            Ask Chanakya free →
          </Link>
        </section>
      </div>
    </div>
  );
}
